import { client } from "../services/client";
import { ENDPOINTS, BASE_URL } from "../config";

// Window handlers for login and logout events
window.handleLogin = async (userId) => {
  console.log('[FCM] handleLogin called for userId:', userId);
  
  // Store the user ID in localStorage
  localStorage.setItem('userId', userId);
  
  // Get FCM token from storage (may have been received from Android earlier)
  const fcmToken = localStorage.getItem('fcmToken') || sessionStorage.getItem('fcmToken');
  const authToken = localStorage.getItem('token');
  
  console.log('[FCM] Login - FCM Token:', fcmToken ? fcmToken.substring(0, 20) + '...' : 'null');
  console.log('[FCM] Login - Auth Token:', authToken ? 'present' : 'missing');
  
  // If we have FCM token and auth token, register immediately
  if (fcmToken && authToken) {
    try {
      await window.registerFCMToken(userId, fcmToken, 'android');
      console.log('[FCM] Token registered successfully after login');
    } catch (error) {
      console.error('[FCM] Error registering token after login:', error);
    }
  } else {
    console.log('[FCM] Waiting for FCM token from Android...');
    // Request FCM token from Android if available
    if (window.Android && typeof window.Android.requestFCMToken === 'function') {
      window.Android.requestFCMToken();
    }
  }
  
  // Store company ID in Android SharedPreferences for post-login splash screen
  const userInfoStr = localStorage.getItem('userInfo');
  if (userInfoStr && window.Android && typeof window.Android.storeCompanyId === 'function') {
    try {
      const userInfo = JSON.parse(userInfoStr);
      const companyId = userInfo?.userType?.company?.id || userInfo?.companyId || userInfo?.company?.id;
      if (companyId) {
        window.Android.storeCompanyId(String(companyId));
      }
    } catch (e) {
      console.error('[Android] Error storing companyId:', e);
    }
  }

  console.log('[FCM] User logged in:', userId);
};

window.handleLogout = async () => {
  try {
    // Clear company ID on Android side
    if (window.Android && typeof window.Android.clearCompanyId === 'function') {
      window.Android.clearCompanyId();
    }

    // Get the current user ID and FCM token before clearing
    const userId = localStorage.getItem('userId');
    const fcmToken = localStorage.getItem('fcmToken') || sessionStorage.getItem('fcmToken');
    const authToken = localStorage.getItem('token');
    
    console.log('[FCM] Logging out - userId:', userId);
    console.log('[FCM] Logging out - FCM token:', fcmToken ? fcmToken.substring(0, 20) + '...' : 'null');
    console.log('[FCM] Logging out - Auth token:', authToken ? 'present' : 'missing');
    
    // Deactivate FCM token if available
    if (fcmToken && authToken && userId) {
      try {
        await window.deactivateFCMToken(userId, fcmToken);
        console.log('[FCM] Token deactivated successfully');
      } catch (error) {
        console.error('[FCM] Error deactivating token:', error);
        // Continue with logout even if deactivation fails
      }
    } else {
      console.log('[FCM] No FCM token or auth token found for deactivation');
    }
    
    // Call Android interface method if available (for clearing Android-side state)
    if (window.Android && typeof window.Android.handleUserLogout === 'function') {
      window.Android.handleUserLogout(BASE_URL || '');
    }
    
    // Clear the user ID and token from storage
    localStorage.removeItem('userId');
    localStorage.removeItem('fcmToken');
    sessionStorage.removeItem('fcmToken');
    
    console.log('[FCM] User logged out successfully');
  } catch (error) {
    console.error('[FCM] Error during logout:', error);
    // Still clear storage even if there's an error
    localStorage.removeItem('userId');
    localStorage.removeItem('fcmToken');
    sessionStorage.removeItem('fcmToken');
  }
};

// FCM Token Management Handlers
window.onFCMTokenReceived = async (token) => {
  console.log('[FCM] Token received from Android:', token ? token.substring(0, 20) + '...' : 'null');
  
  // Store token in both localStorage and sessionStorage for redundancy
  localStorage.setItem('fcmToken', token);
  sessionStorage.setItem('fcmToken', token);
  
  // Check if user is already logged in, if so register the token
  const userId = localStorage.getItem('userId');
  const authToken = localStorage.getItem('token');
  
  console.log('[FCM] onFCMTokenReceived - userId:', userId);
  console.log('[FCM] onFCMTokenReceived - authToken:', authToken ? 'present' : 'missing');
  
  if (userId && authToken && token) {
    try {
      await window.registerFCMToken(userId, token, 'android');
      console.log('[FCM] Token registered successfully on receive');
    } catch (error) {
      console.error('[FCM] Error registering token on receive:', error);
    }
  } else {
    console.log('[FCM] User not logged in yet, token stored for later registration');
  }
};

window.onFCMTokenError = (error) => {
  console.error('[FCM] Token Error:', error);
};

window.onTokenRegistrationRequired = async (userId, token, apiEndpoint) => {
  console.log('[FCM] Token registration required:', { userId, token: token ? token.substring(0, 20) + '...' : 'null' });
  try {
    await window.registerFCMToken(userId, token, 'android');
  } catch (error) {
    console.error('[FCM] Error in token registration:', error);
  }
};

window.onTokenDeletionRequired = async (userId, token, apiEndpoint) => {
  console.log('[FCM] Token deletion required:', { userId, token: token ? token.substring(0, 20) + '...' : 'null' });
  try {
    await window.deactivateFCMToken(userId, token);
  } catch (error) {
    console.error('[FCM] Error in token deletion:', error);
  }
};

window.onTokenRegistrationError = (error) => {
  console.error('[FCM] Token Registration Error:', error);
};

// Function to deactivate FCM token with the backend (marks as inactive)
window.deactivateFCMToken = async (userId, token) => {
  try {
    if (!token) {
      console.error('[FCM] No token available for deactivation');
      return null;
    }

    console.log('[FCM] Deactivating token for userId:', userId);
    
    // Use PATCH to deactivate token (mark as inactive)
    const response = await client.patch(ENDPOINTS.deactivateFCMToken, {
      userId: userId ? userId.toString() : '',
      token: token
    });
    
    console.log('[FCM] Token deactivated successfully:', response?.data);
    return response;
  } catch (error) {
    console.error('[FCM] Error deactivating token:', error);
    throw error;
  }
};

// Function to register FCM token with the backend
window.registerFCMToken = async (userId, token, deviceType = 'android') => {
  try {
    const authToken = localStorage.getItem('token');
    
    if (!authToken) {
      console.error('[FCM] No auth token available for registration');
      return null;
    }
    
    if (!token) {
      console.error('[FCM] No FCM token available for registration');
      return null;
    }

    console.log('[FCM] Registering token - userId:', userId, 'deviceType:', deviceType);
    
    const response = await client.post(ENDPOINTS.registerFCMToken, {
      userId: parseInt(userId),
      token: token,
      deviceType: deviceType,
      isActive: true
    });

    console.log('[FCM] Token registered successfully:', response?.data);
    return response;
  } catch (error) {
    console.error('[FCM] Error registering token:', error);
    throw error;
  }
};

// Function to handle location permission result from Android
const prevOnLocationPermissionResult = window.onLocationPermissionResult;
window.onLocationPermissionResult = (result) => {
  console.log('Location permission result:', result);
  if (typeof prevOnLocationPermissionResult === 'function') {
    try {
      prevOnLocationPermissionResult(result);
    } catch (e) {
      console.error('Error in previous onLocationPermissionResult handler:', e);
    }
  }
};

// Function to handle location updates from Android
const prevOnLocationUpdate = window.onLocationUpdate;
window.onLocationUpdate = (location) => {
  console.log('Location update received:', location);
  if (typeof prevOnLocationUpdate === 'function') {
    try {
      prevOnLocationUpdate(location);
    } catch (e) {
      console.error('Error in previous onLocationUpdate handler:', e);
    }
  }
};

// Function to handle single location result from Android
const prevOnLocationResult = window.onLocationResult;
window.onLocationResult = (location) => {
  console.log('Location result received:', location);
  if (typeof prevOnLocationResult === 'function') {
    try {
      prevOnLocationResult(location);
    } catch (e) {
      console.error('Error in previous onLocationResult handler:', e);
    }
  }
};